// Obrazovka sekce podle cesty: klíč z mapy shell/sekce-cesty.ts (nebo daný cestou routeru);
// sekci, kterou /ja nepovoluje, ukáže „Do této sekce nemáte přístup“; neznámou cestu pošle na Domů.
// Sekce se skutečnou obrazovkou jsou v NACTENI, ostatní zastupuje Zastupna.
//
// Obrazovky se stahují až při prvním otevření sekce (`lina()` — každá sekce je vlastní chunk buildu),
// takže hlavní balík nese jen shell, Domů a společné komponenty a první vykreslení na tabletu nezdržuje
// kód sekcí, které uživatel zrovna nepotřebuje (rozdělení balíku 24. 9. 2026, docs/09 §12). Bez signálu
// se chunky načtou z precache service workeru (offline režim 4.5). Nová sekce se přidá řádkem
// `klic: () => import('./x/X').then((m) => m.X)` do NACTENI — statický import by ji vrátil do hlavního balíku.
import type { Ja } from '@ecoser/shared';
import { useEffect, type ComponentType } from 'react';
import { Navigate, useLocation } from 'react-router';
import { lina, NacitaniCasti } from '../komponenty/NacitaniCasti';
import { usePrihlaseni } from '../prihlaseni/stav-prihlaseni';
import { ocekavanePristani } from '../shell/pristani';
import { klicSekceZCesty } from '../shell/sekce-cesty';
import { NematePristup } from './NematePristup';
import { Zastupna } from './Zastupna';

type Obrazovka = ComponentType<{ ja: Ja }>;

/**
 * Sekce, které už mají obrazovku podle prototypu (F1 1.4/1.5: Zaměstnanci, Nastavení; F3 3.3 Katalog položek, 3.6 Vozidla):
 * klíč sekce → stažení kódu obrazovky. Komponenty z nich dělá `lina()` (OBRAZOVKY níž).
 */
const NACTENI: Readonly<Record<string, () => Promise<Obrazovka>>> = {
  zamestnanci: () => import('./zamestnanci/Zamestnanci').then((m) => m.Zamestnanci),
  nastaveni: () => import('./nastaveni/Nastaveni').then((m) => m.Nastaveni),
  zakaznici: () => import('./zakaznici/Zakaznici').then((m) => m.Zakaznici),
  // Zakázky (F4 4.1): seznam se záložkami, nová zakázka, karta se stavovým automatem.
  zakazky: () => import('./zakazky/Zakazky').then((m) => m.Zakazky),
  // Audity (24. 10. 2026, W80): samostatný modul — přehled, firmy auditorů, detail a plán auditu.
  audity: () => import('./audity/Audity').then((m) => m.Audity),
  kontakty: () => import('./kontakty/Kontakty').then((m) => m.Kontakty),
  // Archiv dokumentů (F6 6.2): otisky vystavených dokumentů s filtry a náhledem.
  archiv_dokumentu: () => import('./archiv/ArchivDokumentu').then((m) => m.ArchivDokumentu),
  // Vzory dokumentů (F6 6.10): galerie tištěných dokumentů, editor bloků s živým náhledem papíru,
  // verzování a připomínky uživatelů.
  vzory_dokumentu: () => import('./vzory/VzoryDokumentu').then((m) => m.VzoryDokumentu),
  asistent: () => import('./asistent/Asistent').then((m) => m.Asistent),
  pravidla_ai: () => import('./asistent/PravidlaAi').then((m) => m.PravidlaAi),
  sklad: () => import('./katalog/KatalogPolozek').then((m) => m.KatalogPolozek),
  vozidla: () => import('./vozidla/Vozidla').then((m) => m.Vozidla),
  // Systém řízení (F11): záložky ISŘ pro správce, Moje školení a hlášení pro ostatní; obrazovky záložek
  // přibývají po krocích 11.1–11.8 (sekce/iso/zalozky, sekce/iso/bloky).
  iso: () => import('./iso/Iso').then((m) => m.Iso),
  // Krok 3.5: Evidence a přeprava (záložka Subjekty — registr) a základ fronty Ke kontrole.
  'sklad_evidence:evidence': () =>
    import('./evidence/EvidenceAPreprava').then((m) => m.EvidenceAPreprava),
  // Krok 9.5: ILNO — karty odpadů, ILNO a knihovna popisů.
  'sklad_evidence:ilno': () => import('./ilno/Ilno').then((m) => m.Ilno),
  ke_kontrole: () => import('./ke-kontrole/KeKontrole').then((m) => m.KeKontrole),
  // Obchod (krok 3.7): Moje firmy s obchodní databází a Aktivity.
  moje_firmy: () => import('./obchod/MojeFirmy').then((m) => m.MojeFirmy),
  interakce: () => import('./obchod/Aktivity').then((m) => m.Aktivity),
  // Krok 3.4: Ceníky (prodejní ceníky a ceník dopravy) a Interní ceník.
  ceniky: () => import('./ceny/Ceniky').then((m) => m.Ceniky),
  interni_cenik: () => import('./ceny/Ceniky').then((m) => m.InterniCenik),
  // Krok 3.9: znalostní báze (předpisy, metodiky, odborná korespondence) — v prototypu není.
  znalostni_baze: () => import('./znalosti/ZnalostniBaze').then((m) => m.ZnalostniBaze),
  // Krok 4.3: Kalendář (měsíc / týden / vlastní rozsah, poznámky a úkoly) na cestě /kalendar (?od=&do=&rezim=).
  kalendar: () => import('./kalendar/Kalendar').then((m) => m.Kalendar),
  // Krok 4.4: Můj den — tablet řidiče / skladníka / koordinátora (/muj-den, /muj-den/{id}/{krok}), přehled pro kancelář.
  muj_den: () => import('./muj-den/MujDen').then((m) => m.MujDen),
  // Krok 4.8: Požadavky na svoz (/svoz-pozadavky?stav=&moje=1&specha=1&novy=1).
  svoz_pozadavky: () => import('./svoz-pozadavky/SvozPozadavky').then((m) => m.SvozPozadavky),
  // Kroky 5.1 a 5.2: Vážení (/vazeni?zalozka=novy|archiv|vozidla|nastaveni) — panel váhy a vážní lístky.
  vazeni: () => import('./vazeni/Vazeni').then((m) => m.Vazeni),
  // Krok 8.1: Náš sklad (/sklad-evidence?zalozka=stav|pohyby&zarizeni=) — stav skladu a žurnál pohybů s korekcí.
  sklad_evidence: () => import('./nas-sklad/NasSklad').then((m) => m.NasSklad),
  // Krok 7.1: Poptávky (/poptavky?skupina=&moje=1&volne=1&hledat=&nova=1) — leady z webu, e‑mailu a telefonu.
  poptavky: () => import('./poptavky/Poptavky').then((m) => m.Poptavky),
  // Krok 10.2: Docházka (/dochazka?zalozka=moje|prehled&mesic=) — příchod, odchod, přestávka, měsíční přehled.
  dochazka: () => import('./dochazka/Dochazka').then((m) => m.Dochazka),
  // Krok 7.2: Nabídky (/nabidky?skupina=&moje=1&hledat=&nova=1) a Pipeline (/pipeline) — nacenění a přehled obchodů.
  nabidky: () => import('./nabidky/Nabidky').then((m) => m.Nabidky),
  pipeline: () => import('./pipeline/Pipeline').then((m) => m.Pipeline),
  // Smlouvy (krok 7.3): seznam se záložkami, generování ze vzoru, příloha č. 1, odeslání k podpisu a podpis.
  smlouvy: () => import('./smlouvy/Smlouvy').then((m) => m.Smlouvy),
  // ECOsmlouvy (krok 7.6): přehled plnění servisních smluv — paušály k vyúčtování, audity, indexace.
  ecosmlouvy: () => import('./ecosmlouvy/Ecosmlouvy').then((m) => m.Ecosmlouvy),
  // Pohledávky (krok 6.7): faktury po splatnosti, upomínky, splátkové kalendáře, průběh vymáhání.
  pohledavky: () => import('./pohledavky/Pohledavky').then((m) => m.Pohledavky),
  // Účetnictví › Banka (krok 6.6): pohyby z banky i z výpisu, párování na faktury, dávky ke čtení.
  ucetnictvi: () => import('./ucetnictvi/Ucetnictvi').then((m) => m.Ucetnictvi),
  // Celní správa (krok 9.7): evidence ZMO, oznámení DA‑215 (§ 50) a měsíční oznámení množství.
  celni_sprava: () => import('./celni/CelniSprava').then((m) => m.CelniSprava),
  // Statistiky (krok 9.9): záložky Řidiči, Vozidla, Evidence a — jen admin a jednatel — Zákazníci, Ziskovost.
  statistiky: () => import('./statistiky/Statistiky').then((m) => m.Statistiky),
  // Hlášení a připomínky (krok 12.5): fronta závad a námětů k aplikaci pro administrátora.
  hlaseni: () => import('./hlaseni/Hlaseni').then((m) => m.Hlaseni),
  // Mapa provozu (krok 11.11, GPS etapa 2 část F): /mapa-provozu?datum= — vozidla, kontejnery, zakázky a audity dne.
  mapa_provozu: () => import('./mapa-provozu/MapaProvozu').then((m) => m.MapaProvozu),
  // Auta na cestě (W95, 31. 10. 2026): skladník „Kdo k nám jede“, řidič „Kde jsou naše auta“ (/auta-na-ceste).
  auta_na_ceste: () => import('./auta-na-ceste/AutaNaCeste').then((m) => m.AutaNaCeste),
  // Export do účetnictví (krok 12.4): vydané doklady za období jako ZIP s ISDOC pro PROFIT, náhled a historie.
  export_ucetni: () =>
    import('./export-ucetni/ExportDoUcetnictvi').then((m) => m.ExportDoUcetnictvi),
  // K vyřízení (05/18, W24): jedna fronta se záložkami Ke kontrole, Hlášení, ISŘ události a Kontroly souladu
  // (/k-vyrizeni?zalozka=&polozka=) nad stávajícími daty — položky se vyřizují ve svých sekcích.
  k_vyrizeni: () => import('./k-vyrizeni/KVyrizeni').then((m) => m.KVyrizeni),
  // Pokladny (krok 6.21): pokladna skladu a kanceláře — zůstatek, příjem k fakturám, převody, uzávěrky, pohyby.
  pokladny: () => import('./pokladny/Pokladny').then((m) => m.Pokladny),
  // Zájemci o práci (pokyn zadavatele 7. 10. 2026): /zamestnanci/zajemci?zajemce={id}&po_lhute=1 — uchazeči o zaměstnání.
  'zamestnanci:zajemci': () => import('./zajemci/ZajemciOPraci').then((m) => m.ZajemciOPraci),
  // Odběratelé (rozhodnutí zadavatele 8. 10. 2026, W40): seznam firem s rolí odběratel → karta na záložce Prodej.
  odberatele: () => import('./odberatele/Odberatele').then((m) => m.Odberatele),
};

const OBRAZOVKY: Readonly<Record<string, Obrazovka>> = Object.fromEntries(
  Object.entries(NACTENI).map(([klic, nacti]) => [klic, lina(nacti)]),
);

/**
 * Začne stahovat kód obrazovky sekce podle cesty (nic nevykreslí). Chyba se tu spolkne — ukáže ji až obrazovka,
 * pokud se kód nestáhne ani při vykreslení.
 */
export function predNacistObrazovku(cesta: string): void {
  const klic = klicSekceZCesty(cesta);
  const nacti = klic ? NACTENI[klic] : undefined;
  if (nacti) void nacti().catch(() => undefined);
}

/** Naposledy otevřené sekce na tomhle zařízení, nejnovější první (localStorage) — předstažení po startu. */
const NEDAVNE_SEKCE = 'ecoser.nedavneSekce';
const NEDAVNYCH_NEJVYS = 4;
/** Za jak dlouho po startu se začnou předstahovat naposledy otevřené sekce (ms) — start má přednost. */
export const PRODLEVA_PREDSTAZENI_MS = 3_000;

function nedavneSekce(): string[] {
  try {
    const seznam: unknown = JSON.parse(window.localStorage.getItem(NEDAVNE_SEKCE) ?? '[]');
    return Array.isArray(seznam) ? seznam.filter((k): k is string => typeof k === 'string') : [];
  } catch {
    return [];
  }
}

function zapamatovatSekci(klic: string): void {
  try {
    const seznam = [klic, ...nedavneSekce().filter((k) => k !== klic)].slice(0, NEDAVNYCH_NEJVYS);
    window.localStorage.setItem(NEDAVNE_SEKCE, JSON.stringify(seznam));
  } catch {
    // localStorage nedostupné — sekce se jen nebudou předstahovat.
  }
}

/**
 * Předstažení kódu obrazovek při startu aplikace (`main.tsx`), aby se rozdělení balíku neprojevilo čekáním:
 *  - sekce z adresy hned, souběžně s ověřením přihlášení (GET /ja) — obnovení stránky, odkaz do sekce,
 *    Obnovit v pruhu nové verze;
 *  - na Domů po novém spuštění hned i cíl přistání z minula (tablet řidiče → Můj den, shell/pristani.ts);
 *  - až je aplikace po startu v klidu, po jedné sekce naposledy otevřené na tomhle zařízení — první přechod
 *    do nich pak nečeká na stažení a spuštění kódu (z cache service workeru to na tabletu bývá 200 ms).
 */
export function predNacistPriStartu(): void {
  const { pathname } = window.location;
  predNacistObrazovku(pathname);
  const pristani = pathname === '/' ? ocekavanePristani() : null;
  if (pristani) predNacistObrazovku(pristani);

  // Přihlašovací a veřejné stránky (podpis, portál podkladů, formuláře) nic dalšího nepotřebují.
  if (pathname !== '/' && !klicSekceZCesty(pathname)) return;
  const zbyva = nedavneSekce();
  const vKlidu = (krok: () => void) => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(krok, { timeout: 10_000 });
    } else {
      window.setTimeout(krok, 200);
    }
  };
  const dalsi = (): void => {
    const klic = zbyva.shift();
    if (klic === undefined) return;
    const nacti = NACTENI[klic];
    if (!nacti) return dalsi();
    void nacti()
      .catch(() => undefined)
      .finally(() => vKlidu(dalsi));
  };
  window.setTimeout(() => vKlidu(dalsi), PRODLEVA_PREDSTAZENI_MS);
}

export function StrankaSekce({ klic: dany }: { klic?: string }) {
  const { ja } = usePrihlaseni();
  const { pathname } = useLocation();
  const klic = dany ?? klicSekceZCesty(pathname);
  const smi = Boolean(ja && klic && (ja.sekce ?? []).includes(klic));

  // Otevřená sekce se předstáhne při dalším startu aplikace (predNacistPriStartu).
  useEffect(() => {
    if (smi && klic && NACTENI[klic]) zapamatovatSekci(klic);
  }, [smi, klic]);

  if (!ja || !klic || klic === 'home') {
    return <Navigate to="/" replace />;
  }
  if (!(ja.sekce ?? []).includes(klic)) {
    return <NematePristup klic={klic} ja={ja} />;
  }
  const Obrazovka = OBRAZOVKY[klic];
  if (!Obrazovka) return <Zastupna klic={klic} ja={ja} />;
  // Klíč sekce: po přechodu do jiné sekce začíná načítání i případná chyba stažení znovu.
  return (
    <NacitaniCasti key={klic}>
      <Obrazovka ja={ja} />
    </NacitaniCasti>
  );
}
